import React, { useMemo, useEffect, useState } from "react";
import axios from "axios";

function CustomPriceSection({ propertyData, setPropertyData }) {
    // ✅ Defaults added so it works safely in BOTH Add + Edit even if some fields are missing initially
    const {
        bedrooms,
        single_beds,
        double_beds,
        super_king_beds,
        required_cleaners,
        is_linen_hire = false,
        is_beach_towel,
        is_dolce_gusto,
        is_nespresso,
        is_custom_price,
        custom_price,
        is_custom_sq = false,
        standard_quantities = [],
    } = propertyData || {};

    // ✅ Only count beach towels if Linen Hire is enabled
    const effectiveBeachTowel = !!is_linen_hire && !!is_beach_towel;

    // --- Load laundry item prices (default_price) ---
    const [priceMap, setPriceMap] = useState({}); // { "Bath Towel": 10, ... }

    useEffect(() => {
        // ✅ Only fetch prices if we actually need them (Custom SQ mode)
        const shouldFetch =
            !!is_custom_sq && Array.isArray(standard_quantities) && standard_quantities.length > 0;

        if (!shouldFetch) return;

        let cancelled = false;

        const fetchLaundryItems = async () => {
            try {
                const res = await axios.get("/api/laundry/laundry-items");
                const map = {};
                (res.data || []).forEach((it) => {
                    const name = String(it?.name || "").trim();
                    const price = Number(it?.default_price);
                    if (!name) return;
                    map[name] = Number.isFinite(price) ? price : 0;
                });
                if (!cancelled) setPriceMap(map);
            } catch (e) {
                console.error("Failed to load laundry item prices:", e);
                // keep empty map; UI will still work, extras cost will be 0 until API works
                if (!cancelled) setPriceMap({});
            }
        };

        fetchLaundryItems();
        return () => {
            cancelled = true;
        };
    }, [is_custom_sq, standard_quantities]);

    // --- Included quantities based on beds/bedrooms (+ beach towel) ---
    const includedQtyMap = useMemo(() => {
        const s = Number(single_beds) || 0;
        const d = Number(double_beds) || 0;
        // Super King: double's quantities, its own sheet/cover sizes.
        const sk = Number(super_king_beds) || 0;
        const b = Number(bedrooms);
        const bedCountForBathmats = Number.isFinite(b) ? b : 0;

        const counts = {
            Bedsheet: d * 1,
            "Duvet Cover": d * 1,
            Pillowcase: s * 2 + (d + sk) * 4,
            "Bath Towel": s * 1 + (d + sk) * 2,
            "Hand Towel": s * 1 + (d + sk) * 2,
            "Face Towel": s * 1 + (d + sk) * 2,
            Bathmat: Math.max(1, bedCountForBathmats + 1),
        };

        if (s > 0) {
            counts["Small Bedsheet"] = s * 1;
            counts["Small Duvet Cover"] = s * 1;
        }
        if (sk > 0) {
            counts["Super King Bedsheet"] = sk * 1;
            counts["Super King Duvet Cover"] = sk * 1;
        }

        if (effectiveBeachTowel) {
            counts["Beach Towel"] = s * 1 + (d + sk) * 2;
        }

        return counts;
    }, [bedrooms, single_beds, double_beds, super_king_beds, effectiveBeachTowel]);

    // --- Extras from Custom SQ beyond included (priced by default_price) ---
    const customSqExtras = useMemo(() => {
        if (!is_custom_sq || !Array.isArray(standard_quantities)) {
            return { extraItemsRows: [], extraItemsCost: 0, hasExtraItems: false };
        }

        // requested map (from popup)
        const requestedMap = {};
        standard_quantities.forEach((x) => {
            const n = String(x?.laundry_item_name || "").trim();
            const c = Number(x?.count) || 0;
            if (n) requestedMap[n] = c;
        });

        let extraItemsCost = 0;
        let hasExtraItems = false;

        const extraItemsRows = Object.keys(requestedMap)
            .map((name) => {
                const requested = requestedMap[name] || 0;
                const included = includedQtyMap[name] || 0;
                const extra = Math.max(0, requested - included);

                if (extra > 0) hasExtraItems = true;

                const unit = Number(priceMap[name] || 0);
                const cost = extra * unit;

                extraItemsCost += cost;

                return { name, extra, unit, cost };
            })
            .filter((r) => r.extra > 0)
            .sort((a, b) => a.name.localeCompare(b.name));

        return { extraItemsRows, extraItemsCost, hasExtraItems };
    }, [is_custom_sq, standard_quantities, includedQtyMap, priceMap]);

    // -----------------------------
    // ✅ OLD LOGIC (UNCHANGED) + new extras added
    // -----------------------------
    const priceDetails = useMemo(() => {
        if (bedrooms === "" || bedrooms === null || bedrooms === undefined) {
            return null;
        }

        const b = Number(bedrooms);
        const cleaners = Number(required_cleaners || 1);

        // Extra cleaner surcharge: for 2BR, any cleaner beyond 1 -> 40 AED each
        const extraCleanerUnitPrice = 40;
        let extraCleaners = 0;
        let extraCleanerCost = 0;

        if (b === 2 && cleaners > 1) {
            extraCleaners = cleaners - 1;
            extraCleanerCost = extraCleaners * extraCleanerUnitPrice;
        }

        // Base by bedrooms
        const baseByBedrooms = {
            0: 198, // Studio
            1: 220,
            2: 320,
            3: 420,
            4: 520,
            5: 620,
            6: 720,
            7: 820,
            8: 920,
        };
        const baseRate = baseByBedrooms[b] ?? 0;

        const safeSingle = Number(single_beds || 0);
        // Super King prices exactly like a Double (PLAN_super_king_beds.md)
        const safeDouble = Number(double_beds || 0) + Number(super_king_beds || 0);

        // Included "double sets"
        // Studio & 1BR -> 1 included
        // 2BR -> 2, 3BR -> 3, etc.
        const includedDoubleBeds = b <= 1 ? 1 : b;

        // ✅ Beach towels only if Linen Hire enabled
        const beachTowelUnitPrice = 7;
        const beachTowelQty = effectiveBeachTowel ? safeDouble * 2 + safeSingle : 0;
        const beachTowelCost = beachTowelQty * beachTowelUnitPrice;

        // Coffee capsules count base: 1 for studio & 1BR, then equal to bedrooms for 2+ BR
        const capsulesBaseCount = b <= 1 ? 1 : b;

        // Dolce Gusto: 8 AED per pack
        const dgUnitPrice = 8;
        const dgQty = is_dolce_gusto ? capsulesBaseCount : 0;
        const dgCost = dgQty * dgUnitPrice;

        // Nespresso: 5 AED per pack
        const nespressoUnitPrice = 5;
        const nespressoQty = is_nespresso ? capsulesBaseCount : 0;
        const nespressoCost = nespressoQty * nespressoUnitPrice;

        // --- Special case: all singles, no doubles, and singles <= included sets ---
        // Example: 2BR with 2 singles instead of 2 doubles -> NO extra cost.
        if (safeDouble === 0 && safeSingle <= includedDoubleBeds) {
            const total =
                baseRate +
                beachTowelCost +
                dgCost +
                nespressoCost +
                extraCleanerCost +
                customSqExtras.extraItemsCost;

            return {
                baseRate,
                includedDoubleBeds,
                extraDoubleBeds: 0,
                extraDoubleBedUnitPrice: 50,
                extraDoubleBedCost: 0,
                pairsUsedForReplacement: 0,
                singleReplacementUnitPrice: 20,
                singleReplacementCost: 0,
                freeSingleReplacements: safeSingle,
                extraSingleBeds: 0,
                extraSingleBedUnitPrice: 35,
                extraSingleBedCost: 0,
                beachTowelQty,
                beachTowelUnitPrice,
                beachTowelCost,
                dgQty,
                dgUnitPrice,
                dgCost,
                nespressoQty,
                nespressoUnitPrice,
                nespressoCost,
                // cleaners surcharge
                cleaners,
                extraCleaners,
                extraCleanerUnitPrice,
                extraCleanerCost,
                // ✅ extras (new)
                extraItemsRows: customSqExtras.extraItemsRows,
                extraItemsCost: customSqExtras.extraItemsCost,
                total,
            };
        }

        // --- General case ---

        const missingDoubles = Math.max(0, includedDoubleBeds - safeDouble);

        // Extra real doubles beyond included
        const extraDoubleBeds = Math.max(0, safeDouble - includedDoubleBeds);
        const extraDoubleBedUnitPrice = 50;
        const extraDoubleBedCost = extraDoubleBeds * extraDoubleBedUnitPrice;

        // Pairs of singles replacing missing doubles (2 singles = 1 double) -> 20 AED each
        const pairReplacements = Math.min(missingDoubles, Math.floor(safeSingle / 2));
        const singlesUsedInPairs = pairReplacements * 2;
        const singleReplacementUnitPrice = 20;
        const singleReplacementCost = pairReplacements * singleReplacementUnitPrice;

        // Remaining singles after counting pairs
        let remainingSingles = safeSingle - singlesUsedInPairs;

        // Singles that just replace a missing double one-by-one, with NO extra cost
        // (e.g. your "third bed is just a single instead of a double" case in 3BR 1D + 3S)
        const freeSingleReplacements = Math.min(
            remainingSingles,
            Math.max(0, missingDoubles - pairReplacements)
        );
        remainingSingles -= freeSingleReplacements;

        // Any further singles beyond that are true extra beds -> 35 AED each
        const extraSingleBeds = remainingSingles;
        const extraSingleBedUnitPrice = 35;
        const extraSingleBedCost = extraSingleBeds * extraSingleBedUnitPrice;

        const total =
            baseRate +
            extraDoubleBedCost +
            singleReplacementCost +
            extraSingleBedCost +
            beachTowelCost +
            dgCost +
            nespressoCost +
            extraCleanerCost +
            customSqExtras.extraItemsCost;

        return {
            baseRate,
            includedDoubleBeds,
            extraDoubleBeds,
            extraDoubleBedUnitPrice,
            extraDoubleBedCost,
            pairsUsedForReplacement: pairReplacements,
            singleReplacementUnitPrice,
            singleReplacementCost,
            freeSingleReplacements,
            extraSingleBeds,
            extraSingleBedUnitPrice,
            extraSingleBedCost,
            beachTowelQty,
            beachTowelUnitPrice,
            beachTowelCost,
            dgQty,
            dgUnitPrice,
            dgCost,
            nespressoQty,
            nespressoUnitPrice,
            nespressoCost,
            // cleaners surcharge
            cleaners,
            extraCleaners,
            extraCleanerUnitPrice,
            extraCleanerCost,
            // ✅ extras (new)
            extraItemsRows: customSqExtras.extraItemsRows,
            extraItemsCost: customSqExtras.extraItemsCost,
            total,
        };
    }, [
        bedrooms,
        single_beds,
        double_beds,
        super_king_beds,
        required_cleaners,
        effectiveBeachTowel,
        is_dolce_gusto,
        is_nespresso,
        customSqExtras.extraItemsCost,
        customSqExtras.extraItemsRows,
    ]);

    // ✅ mustUseCustomPrice (OLD) + also triggers when Custom SQ adds extras
    const mustUseCustomPrice = useMemo(() => {
        if (bedrooms === "" || bedrooms === null || bedrooms === undefined) {
            return false;
        }

        const b = Number(bedrooms);
        const cleaners = Number(required_cleaners || 1);

        const effectiveBedrooms = b === 0 ? 1 : b; // studio counts as 1
        const combinedBeds = Number(single_beds || 0) + Number(double_beds || 0) + Number(super_king_beds || 0);

        const hasExtraBeds = combinedBeds > effectiveBedrooms;
        const hasExtraCleanerForTwoBR = b === 2 && cleaners > 1;
        const hasAddOns =
            effectiveBeachTowel || is_dolce_gusto || is_nespresso || hasExtraCleanerForTwoBR;

        const hasExtraItemsFromSQ = !!customSqExtras.hasExtraItems;

        return hasExtraBeds || hasAddOns || hasExtraItemsFromSQ;
    }, [
        bedrooms,
        single_beds,
        double_beds,
        super_king_beds,
        required_cleaners,
        effectiveBeachTowel,
        is_dolce_gusto,
        is_nespresso,
        customSqExtras.hasExtraItems,
    ]);

    useEffect(() => {
        if (mustUseCustomPrice && !is_custom_price) {
            setPropertyData((prev) => {
                const emptyPrice =
                    prev.custom_price === "" ||
                    prev.custom_price === null ||
                    prev.custom_price === undefined;
                return {
                    ...prev,
                    is_custom_price: true,
                    // Custom price is being auto-forced on (extra beds / add-ons).
                    // Prefill the amount with the calculated all-inclusive price so the
                    // field is never left blank — same value as "Use recommended".
                    // Don't clobber an amount the user (or DB, in edit mode) already set.
                    custom_price:
                        emptyPrice && priceDetails
                            ? Number(priceDetails.total.toFixed(2))
                            : prev.custom_price,
                };
            });
        }
    }, [mustUseCustomPrice, is_custom_price, setPropertyData, priceDetails]);

    const handleToggleCustomPrice = (checked) => {
        // If custom price is mandatory (extra beds or add-ons),
        // don't allow turning it off
        if (!checked && mustUseCustomPrice) {
            return;
        }

        setPropertyData((prev) => ({
            ...prev,
            is_custom_price: checked,
        }));
    };

    const handleCustomPriceChange = (value) => {
        setPropertyData((prev) => ({
            ...prev,
            custom_price: value,
        }));
    };

    const handleUseRecommendedPrice = () => {
        if (!priceDetails) return;

        setPropertyData((prev) => ({
            ...prev,
            is_custom_price: true,
            custom_price: Number(priceDetails.total.toFixed(2)),
        }));
    };

    const durationText = useMemo(() => {
        if (bedrooms === "" || bedrooms === null || bedrooms === undefined) {
            return null;
        }

        const b = Number(bedrooms);
        const cleaners = Number(required_cleaners || 1);

        // Rules:
        // Studio: 2.5 hours
        // 1 bedroom: 3 hours
        // 2 bedroom: 4 hours, or 2.5 hours with 2 cleaners
        // 3 bedrooms: 3 hours
        // 4 bedrooms: 4 hours

        if (b === 0) {
            return "2.5 Hours";
        }
        if (b === 1) {
            return "3 Hours";
        }
        if (b === 2) {
            return cleaners >= 2 ? "2.5 Hours" : "4 Hours";
        }
        if (b === 3) {
            return "3 Hours";
        }
        if (b === 4) {
            return "4 Hours";
        }

        // For 5+ if ever needed, you can extend later
        return null;
    }, [bedrooms, required_cleaners]);

    const extraItemBadges = useMemo(() => {
        const rows = priceDetails?.extraItemsRows || [];
        return rows
            .filter((r) => Number(r.extra) > 0)
            .map((r) => ({
                key: `extra-${r.name}`,
                label: `${r.extra} Extra ${r.name}`,
            }));
    }, [priceDetails?.extraItemsRows]);

    return (
        <div className="form-group property-toggle-group">
            <label className="property-toggle-label">Custom Price</label>

            <div
                className="property-toggle-inline"
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "8px",
                    flexWrap: "wrap",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "20px",
                    }}
                >
                    <label className="switch">
                        <input
                            type="checkbox"
                            checked={!!is_custom_price}
                            onChange={(e) => handleToggleCustomPrice(e.target.checked)}
                        />
                        <span className="slider"></span>
                    </label>

                    {is_custom_price && (
                        <input
                            type="number"
                            className="property-custom-price-input"
                            value={custom_price || ""}
                            onChange={(e) => handleCustomPriceChange(e.target.value)}
                            required
                            onWheel={(e) => e.target.blur()} // Prevent scrolling
                            onKeyDown={(e) =>
                                e.key === "ArrowUp" || e.key === "ArrowDown"
                                    ? e.preventDefault()
                                    : null
                            }
                        />
                    )}
                </div>

                {priceDetails && (
                    <button
                        type="button"
                        onClick={handleUseRecommendedPrice}
                        style={{
                            padding: "6px 10px",
                            fontSize: "0.78rem",
                            borderRadius: "999px",
                            backgroundColor: "#ff5a60",
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                        }}
                    >
                        Use recommended ({priceDetails.total.toFixed(2)} AED)
                    </button>
                )}
            </div>

            {/* Price calculator box */}
            <div
                className="price-calculator-box"
                style={{
                    marginTop: "10px",
                    padding: "10px 12px",
                    borderRadius: "6px",
                    border: "1px solid #e3e3e3",
                    background:
                        "linear-gradient(135deg, #fafafa 0%, #ffffff 40%, #f7f7f7 100%)",
                    fontSize: "0.85rem",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                }}
            >
                {/* Header */}
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "baseline",
                        marginBottom: "6px",
                    }}
                >
                    <div style={{ fontWeight: 600 }}>All Inclusive Price Calculator</div>
                    <div
                        style={{
                            fontSize: "0.75rem",
                            color: "#888",
                            fontStyle: "italic",
                        }}
                    >
                        For reference purposes only
                    </div>
                </div>

                {/* Small summary chips */}
                {priceDetails && (
                    <div
                        style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "6px",
                            marginBottom: "6px",
                        }}
                    >
                        <span
                            style={{
                                padding: "2px 8px",
                                borderRadius: "999px",
                                border: "1px solid #e0e0e0",
                                backgroundColor: "#fff",
                                fontSize: "0.75rem",
                            }}
                        >
                            {bedrooms === 0 ? "Studio" : `${bedrooms} BR`}
                        </span>

                        <span
                            style={{
                                padding: "2px 8px",
                                borderRadius: "999px",
                                border: "1px solid #e0e0e0",
                                backgroundColor: "#fff",
                                fontSize: "0.75rem",
                            }}
                        >
                            {double_beds || 0} Double Bed(s)
                        </span>

                        {Number(super_king_beds || 0) > 0 && (
                            <span
                                style={{
                                    padding: "2px 8px",
                                    borderRadius: "999px",
                                    border: "1px solid #e0e0e0",
                                    backgroundColor: "#fff",
                                    fontSize: "0.75rem",
                                }}
                            >
                                {super_king_beds} Super King Bed(s)
                            </span>
                        )}

                        {Number(single_beds || 0) > 0 && (
                            <span
                                style={{
                                    padding: "2px 8px",
                                    borderRadius: "999px",
                                    border: "1px solid #e0e0e0",
                                    backgroundColor: "#fff",
                                    fontSize: "0.75rem",
                                }}
                            >
                                {single_beds} Single Bed(s)
                            </span>
                        )}

                        <span
                            style={{
                                padding: "2px 8px",
                                borderRadius: "999px",
                                border: "1px solid #e0e0e0",
                                backgroundColor: "#fff",
                                fontSize: "0.75rem",
                            }}
                        >
                            {(required_cleaners || 1)} Cleaner(s)
                        </span>

                        {durationText && (
                            <span
                                style={{
                                    padding: "2px 8px",
                                    borderRadius: "999px",
                                    border: "1px solid #e0e0e0",
                                    backgroundColor: "#fff",
                                    fontSize: "0.75rem",
                                }}
                            >
                                {durationText}
                            </span>
                        )}

                        {effectiveBeachTowel && (
                            <span
                                style={{
                                    padding: "2px 8px",
                                    borderRadius: "999px",
                                    border: "1px solid #ffe0cc",
                                    backgroundColor: "#fff8f2",
                                    fontSize: "0.75rem",
                                }}
                            >
                                LH Beach Towels
                            </span>
                        )}

                        {extraItemBadges.map((b) => (
                            <span
                                key={b.key}
                                style={{
                                    padding: "2px 8px",
                                    borderRadius: "999px",
                                    border: "1px solid #ffe0cc",
                                    backgroundColor: "#fff8f2",
                                    fontSize: "0.75rem",
                                }}
                            >
                                {b.label}
                            </span>
                        ))}

                        {is_dolce_gusto && (
                            <span
                                style={{
                                    padding: "2px 8px",
                                    borderRadius: "999px",
                                    border: "1px solid #dfe9ff",
                                    backgroundColor: "#f4f7ff",
                                    fontSize: "0.75rem",
                                }}
                            >
                                Dolce Gusto
                            </span>
                        )}

                        {is_nespresso && (
                            <span
                                style={{
                                    padding: "2px 8px",
                                    borderRadius: "999px",
                                    border: "1px solid #dfe9ff",
                                    backgroundColor: "#f4f7ff",
                                    fontSize: "0.75rem",
                                }}
                            >
                                Nespresso
                            </span>
                        )}
                    </div>
                )}

                {/* Breakdown + total */}
                {priceDetails && (
                    <div style={{ marginTop: "4px" }}>
                        {(() => {
                            // Bedroom label for text
                            let bedroomLabel = "Standard rate";
                            if (bedrooms === 0)
                                bedroomLabel = "Standard Studio Rate (including amenities & linens)";
                            else if (bedrooms === 1)
                                bedroomLabel = "Standard 1 Bedroom Rate (including amenities & linens)";
                            else if (bedrooms > 1)
                                bedroomLabel = `Standard ${bedrooms} Bedroom Rate (including amenities & linens)`;

                            const rows = [];

                            // Base rate
                            rows.push({
                                label: bedroomLabel,
                                value: `${priceDetails.baseRate.toFixed(2)} AED`,
                            });

                            // Extra double beds
                            if (priceDetails.extraDoubleBeds > 0) {
                                rows.push({
                                    label: "Extra Double Beds",
                                    value: `${priceDetails.extraDoubleBeds} × ${priceDetails.extraDoubleBedUnitPrice} AED`,
                                });
                            }

                            // Singles replacing missing doubles (20 AED per replaced double)
                            if (priceDetails.pairsUsedForReplacement > 0) {
                                rows.push({
                                    label: "Double Beds Replaced by Singles",
                                    value: `${priceDetails.pairsUsedForReplacement} × ${priceDetails.singleReplacementUnitPrice} AED`,
                                });
                            }

                            // Extra singles beyond replacements
                            if (priceDetails.extraSingleBeds > 0) {
                                rows.push({
                                    label: "Extra Single Beds",
                                    value: `${priceDetails.extraSingleBeds} × ${priceDetails.extraSingleBedUnitPrice} AED`,
                                });
                            }

                            // Extra cleaner surcharge for 2BR
                            if (priceDetails.extraCleanerCost > 0) {
                                rows.push({
                                    label: "Additional Cleaner for 2BR",
                                    value: `${priceDetails.extraCleaners} × ${priceDetails.extraCleanerUnitPrice} AED`,
                                });
                            }

                            // Beach towels
                            if (effectiveBeachTowel && priceDetails.beachTowelQty > 0) {
                                rows.push({
                                    label: "Beach Towels",
                                    value: `${priceDetails.beachTowelQty} × ${priceDetails.beachTowelUnitPrice} AED`,
                                });
                            }

                            // Dolce Gusto
                            if (is_dolce_gusto && priceDetails.dgQty > 0) {
                                rows.push({
                                    label: `Nescafe Dolce Gusto Capsules (${priceDetails.dgQty * 4} pcs)`,
                                    value: `${priceDetails.dgQty} pack × ${priceDetails.dgUnitPrice} AED`,
                                });
                            }

                            // Nespresso
                            if (is_nespresso && priceDetails.nespressoQty > 0) {
                                rows.push({
                                    label: `Nespresso Capsules (${priceDetails.nespressoQty * 4} pcs)`,
                                    value: `${priceDetails.nespressoQty} pack × ${priceDetails.nespressoUnitPrice} AED`,
                                });
                            }

                            // ✅ Custom SQ extras (priced per default_price)
                            if (priceDetails.extraItemsRows?.length) {
                                priceDetails.extraItemsRows.forEach((r) => {
                                    rows.push({
                                        label: `Extra ${r.name}`,
                                        value: `${r.extra} × ${Number(r.unit || 0).toFixed(2)} AED`,
                                    });
                                });
                            }

                            return (
                                <>
                                    <div
                                        style={{
                                            fontSize: "0.78rem",
                                            textTransform: "uppercase",
                                            color: "#777",
                                            marginBottom: "2px",
                                            letterSpacing: "0.03em",
                                        }}
                                    >
                                        Price breakdown
                                    </div>

                                    <div
                                        style={{
                                            marginBottom: "6px",
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "2px",
                                        }}
                                    >
                                        {rows.map((row, idx) => (
                                            <div
                                                key={idx}
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    gap: "8px",
                                                }}
                                            >
                                                <span>{row.label}</span>
                                                <span style={{ fontWeight: 500 }}>{row.value}</span>
                                            </div>
                                        ))}
                                    </div>

                                    <div
                                        style={{
                                            paddingTop: "4px",
                                            borderTop: "1px dashed #ddd",
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            marginTop: "4px",
                                        }}
                                    >
                                        <span
                                            style={{
                                                fontSize: "0.8rem",
                                                color: "#555",
                                            }}
                                        >
                                            Total all-inclusive rate
                                        </span>
                                        <span
                                            style={{
                                                fontWeight: 700,
                                                fontSize: "0.95rem",
                                                textAlign: "right",
                                            }}
                                        >
                                            {priceDetails.total.toFixed(2)} AED{" "}
                                            <span
                                                style={{
                                                    fontWeight: 500,
                                                    fontSize: "0.8rem",
                                                    color: "#777",
                                                    marginLeft: "4px",
                                                }}
                                            >
                                                {`(incl. VAT: ${(priceDetails.total * 1.05).toFixed(2)} AED)`}
                                            </span>
                                        </span>
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                )}

                {!priceDetails && (
                    <div
                        style={{
                            fontStyle: "italic",
                            color: "#777",
                            marginTop: "4px",
                        }}
                    >
                        Select bedrooms and beds to see a suggested price.
                    </div>
                )}
            </div>
        </div>
    );
}

export default CustomPriceSection;
